<script setup lang="ts">
import type { ItemType } from '~/types'

const favourites = useFavouritesStore()

type Props = {
  id: string
  type: ItemType
  to: string
  title: string
  image: {
    src?: string | null
    alt?: string | null
  }
  favourited?: string | null
  refetch?: () => Promise<unknown>
  class?: string
  contentTextClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  favourited: undefined,
  refetch: undefined,
  class: undefined,
  contentTextClass: 'flex-col',
})

const classes = computed(() => ({
  'app-card-inline': true,
  [`${props.class}`]: !!props.class,
}))

const toggleFavourite = async (event: MouseEvent) => {
  event.preventDefault()

  await favourites.toggleFavourite({
    id: props.id,
    type: props.type,
    favouriteId: props.favourited,
  })

  if (props.refetch) await props.refetch()
}
</script>

<template>
  <article :class="classes">
    <NuxtLink class="app-card-inline__link" :to="to">
      <AppImage
        class="app-card-inline__image"
        ratio="2:3"
        :src="image.src"
        :alt="image.alt"
      >
        <AppButtonLike
          class="app-card-inline__like-button"
          size="xs"
          :active="!!favourited"
          @click="toggleFavourite"
        />
      </AppImage>

      <div class="app-card-inline__content">
        <h3 class="app-card-inline__content-title h4 text-balance">
          {{ title }}
        </h3>

        <div
          class="app-card-inline__content-text clean-cms-styles flex-row"
          :class="contentTextClass"
        >
          <slot></slot>
        </div>
      </div>
    </NuxtLink>
  </article>
</template>

<style lang="scss">
@use 'sass:math';

@import '@boats/ui/styles/config';

.app-card-inline__image {
  width: 2.5rem;
  margin-right: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    background-color: $black;
    opacity: 0;
    @include transition('opacity');
  }

  img {
    @include transition('transform');
  }

  @include media-breakpoint-up(md) {
    width: 6.75rem;
    margin-right: 1.5rem;
  }
}

.app-card-inline__content {
  flex: 1 1 0%;
}

.app-card-inline__content-title {
  margin-bottom: 0.25rem;

  @include media-breakpoint-up(md) {
    margin-bottom: 0.5rem;
  }
}

.app-card-inline__content-text {
  p {
    font-family: $font-barlow;
    font-size: 0.875rem;
    line-height: 1.42857143;
    margin: 0;
  }

  @include media-breakpoint-up(md) {
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    p {
      font-size: 1rem;
      line-height: 1.5;
      margin: 0 2.5rem 0 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}

.app-card-inline__like-button {
  display: none;

  @include media-breakpoint-up(md) {
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;
    display: block;
    z-index: 1;
  }
}

.app-card-inline__link {
  display: flex;
  align-items: flex-start;
  text-decoration: none !important;

  &:hover {
    .app-card-inline__image {
      img {
        transform: scale(1.05);
      }

      &::after {
        opacity: 0.1;
      }
    }

    .app-card-inline__content-title {
      text-decoration: underline;
    }
  }

  &:active {
    .app-card-inline__image {
      &::after {
        opacity: 0.65;
      }
    }
  }
}
</style>
